import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Course, MerchProduct, ImpactSnapshot, DonationCampaign } from '@juvo/schemas';
import { pickLocale } from '@juvo/utils';
import { store } from '../store.js';
import '../layouts/site-chrome.js';

@customElement('jv-admin-page')
export class JvAdminPage extends LitElement {
  static styles = css`
    .layout {
      display: grid;
      gap: 1.5rem;
    }
    @media (min-width: 860px) {
      .layout {
        grid-template-columns: 14rem 1fr;
      }
    }
    nav {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    a {
      text-decoration: none;
      color: var(--jv-color-muted);
    }
    a[aria-current='page'] {
      color: var(--jv-color-accent);
      font-weight: 600;
    }
    h1 {
      font-size: var(--jv-fs-xl);
      margin: 0 0 1rem;
    }
    .list {
      display: grid;
      gap: 0.75rem;
      margin-top: 1rem;
    }
    .form-panel {
      background: var(--jv-color-surface);
      border: var(--jv-border-w) solid var(--jv-color-border);
      padding: 1.25rem;
      margin-bottom: 1.5rem;
    }
    .form-grid {
      display: grid;
      gap: 0.75rem;
    }
    .grid-2 {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
    @media (min-width: 600px) {
      .grid-2 {
        grid-template-columns: 1fr 1fr;
      }
    }
    .notice {
      padding: 0.75rem;
      margin-bottom: 1rem;
      font-weight: 600;
      border-radius: 2px;
    }
    .notice-success {
      background: #e6f4ea;
      color: #137333;
    }
    .notice-error {
      background: #fce8e6;
      color: #c5221f;
    }
    .item-card {
      background: var(--jv-color-surface);
      border: var(--jv-border-w) solid var(--jv-color-border);
      padding: 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    @media (min-width: 640px) {
      .item-card {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
      }
    }
    .item-preview {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .item-thumb {
      width: 48px;
      height: 48px;
      object-fit: contain;
      background: var(--jv-color-bg);
      border: 1px solid var(--jv-color-border);
      border-radius: 4px;
      padding: 4px;
    }
    .item-actions {
      display: flex;
      gap: 0.5rem;
      align-items: center;
    }
    .edit-drawer {
      background: var(--jv-color-bg);
      border: 1px dashed var(--jv-color-accent);
      padding: 1.25rem;
      margin-top: 1rem;
      border-radius: 4px;
    }
    .image-picker-box {
      border: 1px dashed var(--jv-color-border);
      padding: 1rem;
      background: var(--jv-color-bg);
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .image-preview-row {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .image-preview-row img {
      max-width: 96px;
      max-height: 96px;
      object-fit: contain;
      border: 1px solid var(--jv-color-border);
      background: var(--jv-color-surface);
      padding: 4px;
    }
  `;

  @property() section:
    | 'courses'
    | 'merch'
    | 'metrics'
    | 'enrollment'
    | 'assets'
    | 'jvc'
    | 'partners' = 'courses';

  @state() private courses: Course[] = [];
  @state() private merch: MerchProduct[] = [];
  @state() private impact: ImpactSnapshot | null = null;
  @state() private campaign: DonationCampaign | null = null;
  @state() private statusMsg = '';
  @state() private isError = false;

  // Track currently edited item
  @state() private editingCourseSlug: string | null = null;
  @state() private editingMerchSku: string | null = null;

  // New/Edit Course Form State
  @state() private cSlug = '';
  @state() private cTitleFr = '';
  @state() private cTitleEn = '';
  @state() private cDescFr = '';
  @state() private cDescEn = '';
  @state() private cTrack = 'technical';
  @state() private cLevel = 'beginner';
  @state() private cWeeks = '4';

  // New/Edit Merch Form State
  @state() private mSku = '';
  @state() private mNameFr = '';
  @state() private mNameEn = '';
  @state() private mDescFr = '';
  @state() private mDescEn = '';
  @state() private mPrice = '25';
  @state() private mImage = '/assets/logo_full.png';

  // Metrics Form State
  @state() private metKg = '0';
  @state() private metCohorts = '0';
  @state() private metPartners = '0';
  @state() private metDeployments = '0';
  @state() private metTrained = '0';
  @state() private metJvcIssued = '0';
  @state() private campFunds = '0';
  @state() private campTarget = '8000';

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
    if (!store.isStaff) {
      history.replaceState({}, '', '/login?next=/admin');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }
    this.loadData();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }

  private unsubscribe?: () => void;

  private async loadData() {
    try {
      const [courses, merch, impact, campaign] = await Promise.all([
        store.client.getCourses(),
        store.client.getMerch(),
        store.client.getImpact(),
        store.client.getDonationCampaign(),
      ]);
      this.courses = courses;
      this.merch = merch;
      this.impact = impact;
      this.campaign = campaign;

      if (impact) {
        this.metKg = String(impact.kgRefurbished);
        this.metCohorts = String(impact.cohortsRun);
        this.metPartners = String(impact.partnersActive);
        this.metDeployments = String(impact.deployments);
        this.metTrained = String(impact.peopleTrained);
        this.metJvcIssued = String(impact.jvcIssued);
      }
      if (campaign) {
        this.campFunds = String(campaign.fundsRaisedUsd);
        this.campTarget = String(campaign.fundsTargetUsd);
      }
    } catch (err) {
      console.error(err);
    }
  }

  render() {
    const t = store.t.bind(store);
    const links = [
      ['/admin/courses', t('admin.courses'), 'courses'],
      ['/admin/merch', t('admin.merch'), 'merch'],
      ['/admin/metrics', t('admin.metrics'), 'metrics'],
      ['/admin/enrollment', t('admin.enrollment'), 'enrollment'],
      ['/admin/assets', t('admin.assets'), 'assets'],
      ['/admin/jvc', t('admin.jvc'), 'jvc'],
      ['/admin/partners', t('admin.partners'), 'partners'],
    ] as const;

    return html`
      <jv-page>
        <div class="layout">
          <nav>
            ${links.map(
              ([href, label, key]) => html`
                <a href=${href} aria-current=${this.section === key ? 'page' : 'false'}>${label}</a>
              `,
            )}
          </nav>
          <section>
            ${this.statusMsg
              ? html`<div class="notice ${this.isError ? 'notice-error' : 'notice-success'}">
                  ${this.statusMsg}
                </div>`
              : ''}
            ${this.body()}
          </section>
        </div>
      </jv-page>
    `;
  }

  private body() {
    const t = store.t.bind(store);

    if (this.section === 'courses') {
      return html`
        <h1>${t('admin.courses')}</h1>
        <div class="form-panel">
          <h3 style="margin: 0 0 1rem;">
            ${this.editingCourseSlug ? `Edit Course: ${this.editingCourseSlug}` : 'Add New Course'}
          </h3>
          <form @submit=${this.onSaveCourse} class="form-grid">
            <div class="grid-2">
              <jv-form-field
                label="Slug (e.g. biomed-hardware-101)"
                required
                .value=${this.cSlug}
                ?disabled=${Boolean(this.editingCourseSlug)}
                @jv-input=${(e: CustomEvent) => (this.cSlug = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label="Duration (weeks)"
                type="number"
                required
                .value=${this.cWeeks}
                @jv-input=${(e: CustomEvent) => (this.cWeeks = e.detail.value)}
              ></jv-form-field>
            </div>

            <div class="grid-2">
              <jv-form-field
                label="Title (French)"
                required
                .value=${this.cTitleFr}
                @jv-input=${(e: CustomEvent) => (this.cTitleFr = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label="Title (English)"
                required
                .value=${this.cTitleEn}
                @jv-input=${(e: CustomEvent) => (this.cTitleEn = e.detail.value)}
              ></jv-form-field>
            </div>

            <div class="grid-2">
              <jv-form-field
                label="Description (French)"
                required
                multiline
                .value=${this.cDescFr}
                @jv-input=${(e: CustomEvent) => (this.cDescFr = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label="Description (English)"
                required
                multiline
                .value=${this.cDescEn}
                @jv-input=${(e: CustomEvent) => (this.cDescEn = e.detail.value)}
              ></jv-form-field>
            </div>

            <div class="grid-2">
              <div>
                <label style="font-size:0.85rem; font-family:var(--jv-font-mono);">Track</label>
                <select
                  style="width:100%; padding:0.5rem; background:var(--jv-color-bg); color:var(--jv-color-text); border:1px solid var(--jv-color-border);"
                  .value=${this.cTrack}
                  @change=${(e: Event) => (this.cTrack = (e.target as HTMLSelectElement).value)}
                >
                  <option value="technical">Technical</option>
                  <option value="digital-health">Digital Health</option>
                  <option value="ai">AI</option>
                  <option value="health-tech">Health Tech</option>
                  <option value="embedded">Embedded</option>
                </select>
              </div>
              <div>
                <label style="font-size:0.85rem; font-family:var(--jv-font-mono);">Level</label>
                <select
                  style="width:100%; padding:0.5rem; background:var(--jv-color-bg); color:var(--jv-color-text); border:1px solid var(--jv-color-border);"
                  .value=${this.cLevel}
                  @change=${(e: Event) => (this.cLevel = (e.target as HTMLSelectElement).value)}
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
            </div>

            <div style="margin-top:0.5rem; display:flex; gap:0.5rem;">
              <jv-button type="submit" variant="primary">
                ${this.editingCourseSlug ? 'Update Course' : 'Create Course'}
              </jv-button>
              ${this.editingCourseSlug
                ? html`
                    <jv-button type="button" @click=${this.cancelEditCourse}>Cancel</jv-button>
                  `
                : ''}
            </div>
          </form>
        </div>

        <h3 style="margin: 1.5rem 0 0.5rem;">Existing Courses (${this.courses.length})</h3>
        <div class="list">
          ${this.courses.map(
            (course) => html`
              <div class="item-card">
                <div>
                  <strong>${pickLocale(course.title, store.locale)}</strong>
                  <p style="margin: 0.25rem 0 0; font-size: 0.85rem; color: var(--jv-color-muted);">
                    <code>${course.slug}</code> • ${course.track} • ${course.level} • ${course.durationWeeks} weeks
                  </p>
                </div>
                <div class="item-actions">
                  <jv-button type="button" @click=${() => this.startEditCourse(course)}>
                    Edit Course
                  </jv-button>
                  <jv-button
                    type="button"
                    style="--jv-color-border:#ea4335; color:#c5221f;"
                    @click=${() => this.onDeleteCourse(course.slug)}
                  >
                    Delete
                  </jv-button>
                  <jv-button href="/courses/${course.slug}">View Page</jv-button>
                </div>
              </div>
            `,
          )}
        </div>
      `;
    }

    if (this.section === 'merch') {
      return html`
        <h1>${t('admin.merch')}</h1>
        <div class="form-panel">
          <h3 style="margin: 0 0 1rem;">
            ${this.editingMerchSku ? `Edit Product: ${this.editingMerchSku}` : 'Add Store Merchandise'}
          </h3>
          <form @submit=${this.onSaveMerch} class="form-grid">
            <div class="grid-2">
              <jv-form-field
                label="SKU (e.g. JV-TEE-02)"
                required
                .value=${this.mSku}
                ?disabled=${Boolean(this.editingMerchSku)}
                @jv-input=${(e: CustomEvent) => (this.mSku = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label="Price (USD)"
                type="number"
                required
                .value=${this.mPrice}
                @jv-input=${(e: CustomEvent) => (this.mPrice = e.detail.value)}
              ></jv-form-field>
            </div>

            <div class="grid-2">
              <jv-form-field
                label="Name (French)"
                required
                .value=${this.mNameFr}
                @jv-input=${(e: CustomEvent) => (this.mNameFr = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label="Name (English)"
                required
                .value=${this.mNameEn}
                @jv-input=${(e: CustomEvent) => (this.mNameEn = e.detail.value)}
              ></jv-form-field>
            </div>

            <div class="grid-2">
              <jv-form-field
                label="Description (French)"
                required
                multiline
                .value=${this.mDescFr}
                @jv-input=${(e: CustomEvent) => (this.mDescFr = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label="Description (English)"
                required
                multiline
                .value=${this.mDescEn}
                @jv-input=${(e: CustomEvent) => (this.mDescEn = e.detail.value)}
              ></jv-form-field>
            </div>

            <div class="image-picker-box">
              <label style="font-size:0.85rem; font-family:var(--jv-font-mono); font-weight:600;">
                Product Image (Upload from device or enter path / URL)
              </label>
              <div class="image-preview-row">
                <img src=${this.mImage} alt="Product preview" />
                <div style="display:flex; flex-direction:column; gap:0.5rem; flex:1;">
                  <input
                    type="file"
                    accept="image/*"
                    @change=${this.onDeviceImageUpload}
                    style="font-size:0.85rem; font-family:var(--jv-font-mono);"
                  />
                  <jv-form-field
                    label="Or image path / URL"
                    .value=${this.mImage}
                    @jv-input=${(e: CustomEvent) => (this.mImage = e.detail.value)}
                  ></jv-form-field>
                </div>
              </div>
            </div>

            <div style="margin-top:0.5rem; display:flex; gap:0.5rem;">
              <jv-button type="submit" variant="primary">
                ${this.editingMerchSku ? 'Update Merchandise' : 'Add Merchandise'}
              </jv-button>
              ${this.editingMerchSku
                ? html`
                    <jv-button type="button" @click=${this.cancelEditMerch}>Cancel</jv-button>
                  `
                : ''}
            </div>
          </form>
        </div>

        <h3 style="margin: 1.5rem 0 0.5rem;">Active Products (${this.merch.length})</h3>
        <div class="list">
          ${this.merch.map(
            (item) => html`
              <div class="item-card">
                <div class="item-preview">
                  <img src=${item.image} alt=${item.sku} class="item-thumb" />
                  <div>
                    <strong>${pickLocale(item.name, store.locale)}</strong>
                    <p style="margin: 0.25rem 0 0; font-size: 0.85rem; color: var(--jv-color-muted);">
                      <code>${item.sku}</code> • $${item.priceUsd}
                    </p>
                  </div>
                </div>
                <div class="item-actions">
                  <span style="font-family: var(--jv-font-mono); font-size: 0.9rem; font-weight:600; margin-right: 0.5rem;">
                    $${item.priceUsd}
                  </span>
                  <jv-button type="button" @click=${() => this.startEditMerch(item)}>
                    Edit Item
                  </jv-button>
                  <jv-button
                    type="button"
                    style="--jv-color-border:#ea4335; color:#c5221f;"
                    @click=${() => this.onDeleteMerch(item.sku)}
                  >
                    Delete
                  </jv-button>
                </div>
              </div>
            `,
          )}
        </div>
      `;
    }

    if (this.section === 'metrics') {
      return html`
        <h1>${t('admin.metrics')}</h1>
        <div class="form-panel">
          <h3 style="margin: 0 0 1rem;">Update Live Impact & Measurable Metrics</h3>
          <p style="color:var(--jv-color-muted); font-size:0.9rem; margin-bottom:1rem;">
            Real actual numbers displayed publicly on the homepage, /impact, and /donate.
          </p>
          <form @submit=${this.onUpdateMetrics} class="form-grid">
            <div class="grid-2">
              <jv-form-field
                label="Kilograms Refurbished (kg)"
                type="number"
                required
                .value=${this.metKg}
                @jv-input=${(e: CustomEvent) => (this.metKg = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label="Cohorts Completed"
                type="number"
                required
                .value=${this.metCohorts}
                @jv-input=${(e: CustomEvent) => (this.metCohorts = e.detail.value)}
              ></jv-form-field>
            </div>

            <div class="grid-2">
              <jv-form-field
                label="Active Clinic Partners"
                type="number"
                required
                .value=${this.metPartners}
                @jv-input=${(e: CustomEvent) => (this.metPartners = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label="Clinic Deployments"
                type="number"
                required
                .value=${this.metDeployments}
                @jv-input=${(e: CustomEvent) => (this.metDeployments = e.detail.value)}
              ></jv-form-field>
            </div>

            <div class="grid-2">
              <jv-form-field
                label="People Trained"
                type="number"
                required
                .value=${this.metTrained}
                @jv-input=${(e: CustomEvent) => (this.metTrained = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label="JVC Issued"
                type="number"
                required
                .value=${this.metJvcIssued}
                @jv-input=${(e: CustomEvent) => (this.metJvcIssued = e.detail.value)}
              ></jv-form-field>
            </div>

            <h4 style="margin: 1rem 0 0.25rem;">Fundraising Campaign</h4>
            <div class="grid-2">
              <jv-form-field
                label="Funds Raised (USD)"
                type="number"
                required
                .value=${this.campFunds}
                @jv-input=${(e: CustomEvent) => (this.campFunds = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label="Funds Target (USD)"
                type="number"
                required
                .value=${this.campTarget}
                @jv-input=${(e: CustomEvent) => (this.campTarget = e.detail.value)}
              ></jv-form-field>
            </div>

            <div style="margin-top: 1rem;">
              <jv-button type="submit" variant="primary">Save Measurable Values</jv-button>
            </div>
          </form>
        </div>
      `;
    }

    const headings = {
      enrollment: t('admin.enrollment'),
      assets: t('admin.assets'),
      jvc: t('admin.jvc'),
      partners: t('admin.partners'),
    };
    return html`
      <h1>${headings[this.section as keyof typeof headings]}</h1>
      <jv-empty-state heading=${t('admin.phase2')}></jv-empty-state>
    `;
  }

  // Edit Course Helpers
  private startEditCourse(course: Course) {
    this.editingCourseSlug = course.slug;
    this.cSlug = course.slug;
    this.cTitleFr = course.title?.fr || '';
    this.cTitleEn = course.title?.en || '';
    this.cDescFr = course.description?.fr || '';
    this.cDescEn = course.description?.en || '';
    this.cTrack = course.track || 'technical';
    this.cLevel = course.level || 'beginner';
    this.cWeeks = String(course.durationWeeks || 4);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private cancelEditCourse = () => {
    this.editingCourseSlug = null;
    this.cSlug = '';
    this.cTitleFr = '';
    this.cTitleEn = '';
    this.cDescFr = '';
    this.cDescEn = '';
    this.cWeeks = '4';
  };

  private onSaveCourse = async (e: Event) => {
    e.preventDefault();
    this.statusMsg = '';
    try {
      if (this.editingCourseSlug) {
        const updated = await store.client.updateCourse(this.editingCourseSlug, {
          title: { fr: this.cTitleFr.trim(), en: this.cTitleEn.trim() },
          description: { fr: this.cDescFr.trim(), en: this.cDescEn.trim() },
          track: this.cTrack as any,
          level: this.cLevel as any,
          durationWeeks: parseInt(this.cWeeks, 10) || 4,
        });
        this.courses = this.courses.map((c) => (c.slug === this.editingCourseSlug ? updated : c));
        this.statusMsg = `Course "${this.editingCourseSlug}" updated successfully!`;
        this.cancelEditCourse();
      } else {
        const created = await store.client.createCourse({
          slug: this.cSlug.trim(),
          title: { fr: this.cTitleFr.trim(), en: this.cTitleEn.trim() },
          description: { fr: this.cDescFr.trim(), en: this.cDescEn.trim() },
          track: this.cTrack as any,
          level: this.cLevel as any,
          durationWeeks: parseInt(this.cWeeks, 10) || 4,
          programmeType: 'workshop',
          deliveryMode: 'in-person',
          certificateType: 'juvo',
          published: true,
          tags: [],
          location: 'Faso Kanu, Bamako',
        });
        this.courses = [...this.courses, created];
        this.statusMsg = `Course "${this.cSlug}" created successfully!`;
        this.cancelEditCourse();
      }
      this.isError = false;
    } catch (err: any) {
      this.statusMsg = err?.message || 'Failed to save course';
      this.isError = true;
    }
  };

  // Edit Merch Helpers
  private startEditMerch(item: MerchProduct) {
    this.editingMerchSku = item.sku;
    this.mSku = item.sku;
    this.mNameFr = item.name?.fr || '';
    this.mNameEn = item.name?.en || '';
    this.mDescFr = item.description?.fr || '';
    this.mDescEn = item.description?.en || '';
    this.mPrice = String(item.priceUsd);
    this.mImage = item.image;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private cancelEditMerch = () => {
    this.editingMerchSku = null;
    this.mSku = '';
    this.mNameFr = '';
    this.mNameEn = '';
    this.mDescFr = '';
    this.mDescEn = '';
    this.mPrice = '25';
    this.mImage = '/assets/logo_full.png';
  };

  private onDeviceImageUpload = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        this.mImage = reader.result;
      }
    };
    reader.readAsDataURL(file);
  };

  private onSaveMerch = async (e: Event) => {
    e.preventDefault();
    this.statusMsg = '';
    try {
      if (this.editingMerchSku) {
        const updated = await store.client.updateMerchProduct(this.editingMerchSku, {
          name: { fr: this.mNameFr.trim(), en: this.mNameEn.trim() },
          description: { fr: this.mDescFr.trim(), en: this.mDescEn.trim() },
          priceUsd: parseFloat(this.mPrice) || 25,
          image: this.mImage.trim(),
        });
        this.merch = this.merch.map((m) => (m.sku === this.editingMerchSku ? updated : m));
        this.statusMsg = `Merchandise "${this.editingMerchSku}" updated successfully!`;
        this.cancelEditMerch();
      } else {
        const created = await store.client.createMerchProduct({
          sku: this.mSku.trim().toUpperCase(),
          name: { fr: this.mNameFr.trim(), en: this.mNameEn.trim() },
          description: { fr: this.mDescFr.trim(), en: this.mDescEn.trim() },
          priceUsd: parseFloat(this.mPrice) || 25,
          image: this.mImage.trim(),
        });
        this.merch = [...this.merch, created];
        this.statusMsg = `Merchandise "${this.mSku}" added to catalog!`;
        this.cancelEditMerch();
      }
      this.isError = false;
    } catch (err: any) {
      this.statusMsg = err?.message || 'Failed to save merchandise';
      this.isError = true;
    }
  };

  private onDeleteCourse = async (slug: string) => {
    if (!confirm(`Are you sure you want to delete course "${slug}"?`)) return;
    this.statusMsg = '';
    try {
      await store.client.deleteCourse(slug);
      this.courses = this.courses.filter((c) => c.slug !== slug);
      if (this.editingCourseSlug === slug) {
        this.cancelEditCourse();
      }
      this.statusMsg = `Course "${slug}" deleted successfully!`;
      this.isError = false;
    } catch (err: any) {
      this.statusMsg = err?.message || 'Failed to delete course';
      this.isError = true;
    }
  };

  private onDeleteMerch = async (sku: string) => {
    if (!confirm(`Are you sure you want to delete merchandise item "${sku}"?`)) return;
    this.statusMsg = '';
    try {
      await store.client.deleteMerchProduct(sku);
      this.merch = this.merch.filter((m) => m.sku !== sku);
      if (this.editingMerchSku === sku) {
        this.cancelEditMerch();
      }
      this.statusMsg = `Merchandise "${sku}" deleted successfully!`;
      this.isError = false;
    } catch (err: any) {
      this.statusMsg = err?.message || 'Failed to delete merchandise';
      this.isError = true;
    }
  };

  private onUpdateMetrics = async (e: Event) => {
    e.preventDefault();
    this.statusMsg = '';
    try {
      await Promise.all([
        store.client.updateImpact({
          kgRefurbished: parseInt(this.metKg, 10) || 0,
          cohortsRun: parseInt(this.metCohorts, 10) || 0,
          partnersActive: parseInt(this.metPartners, 10) || 0,
          deployments: parseInt(this.metDeployments, 10) || 0,
          peopleTrained: parseInt(this.metTrained, 10) || 0,
          jvcIssued: parseInt(this.metJvcIssued, 10) || 0,
        }),
        store.client.updateCampaign({
          fundsRaisedUsd: parseFloat(this.campFunds) || 0,
          fundsTargetUsd: parseFloat(this.campTarget) || 8000,
        }),
      ]);
      this.statusMsg = 'Measurable values updated successfully!';
      this.isError = false;
    } catch (err: any) {
      this.statusMsg = err?.message || 'Failed to update metrics';
      this.isError = true;
    }
  };
}
